import type { DataVisualizationPaletteColorName } from '../../shared/MobiusColors'
import {
  ExtendedPaletteColorName,
  NeutralPaletteColorName,
} from '../../shared/MobiusColors'
import { SizePaletteCssVarNames } from './sizePalette'
import { TypePaletteCssVarNames } from './typePalette'

const shadowColors = {
  blackShadow: 'rgba(0, 0, 0, 0.06)',
  blackUnderShadow: 'rgba(0, 0, 0, 0.02)',
  darkBlackShadow: 'rgba(0, 0, 0, 0.6)',
}

export type ThemeColorToken =
  | NeutralColorToken
  | ExtendedPaletteColorName
  | DataVisualizationPaletteColorName

/**
 * A type for the colors that we use in places that have semantic meaning, e.g. text that should
 * change color to match whether an instrument's returns are up (positive) or down (negative).
 * These will almost always be of type ExtendedPaletteColorName, but may occasionally be
 * overridden to be a NeutralColorToken (e.g. if a TextButton is told to render in FG1).
 */
export type SemanticPalette = {
  primary: ThemeColorToken
  success: ThemeColorToken
  error: ThemeColorToken
  positive: ThemeColorToken
  negative: ThemeColorToken
  scary: ThemeColorToken
}

/**
 * A type for our core foreground and background colors.
 */
export type NeutralPalette = {
  fg1: NeutralPaletteColorName
  fg2: NeutralPaletteColorName
  fg3: NeutralPaletteColorName
  bg3: NeutralPaletteColorName
  bg2: NeutralPaletteColorName
  bg1: NeutralPaletteColorName
}

/**
 * This is the type for our core theme object, which comprises all the pieces of a color
 * scheme for a Robinhood app.
 */
export type ProductTheme = {
  isDark: boolean
  // In some themes, the neutral colors are different inside elevated cards.
  getNeutralColorPalette: (elevation: number) => NeutralPalette
  semanticColors: SemanticPalette
  shadowColors: {
    s1: string
    s2: string
  }
}

/**
 * This is the primary theme for our apps.
 */
export const lightTheme: ProductTheme = {
  getNeutralColorPalette: () => ({
    bg1: NeutralPaletteColorName.DayBg1,
    bg2: NeutralPaletteColorName.DayBg2,
    bg3: NeutralPaletteColorName.DayBg3,
    fg1: NeutralPaletteColorName.DayFg1,
    fg2: NeutralPaletteColorName.DayFg2,
    fg3: NeutralPaletteColorName.DayFg3,
  }),

  isDark: false,

  semanticColors: {
    error: ExtendedPaletteColorName.Joule,
    negative: ExtendedPaletteColorName.Joule,
    positive: ExtendedPaletteColorName.Prime,
    primary: ExtendedPaletteColorName.Prime,
    scary: ExtendedPaletteColorName.Joule,
    success: ExtendedPaletteColorName.Prime,
  },

  shadowColors: {
    s1: shadowColors.blackShadow,
    s2: shadowColors.blackUnderShadow,
  },
}

/**
 * This is the dark theme for our apps. For example, an app might use this outside of
 * market hours.
 */
export const darkTheme: ProductTheme = {
  getNeutralColorPalette: elevation => {
    if (elevation > 0) {
      // NOTE(jare): Currently, the web design system only supports going up
      // 1 level of elevation. This may change in the future.
      return {
        bg1: NeutralPaletteColorName.ElevatedNightBg1,
        bg2: NeutralPaletteColorName.ElevatedNightBg2,
        bg3: NeutralPaletteColorName.ElevatedNightBg3,
        fg1: NeutralPaletteColorName.ElevatedNightFg1,
        fg2: NeutralPaletteColorName.ElevatedNightFg2,
        fg3: NeutralPaletteColorName.ElevatedNightFg3,
      }
    }

    return {
      bg1: NeutralPaletteColorName.NightBg1,
      bg2: NeutralPaletteColorName.NightBg2,
      bg3: NeutralPaletteColorName.NightBg3,
      fg1: NeutralPaletteColorName.NightFg1,
      fg2: NeutralPaletteColorName.NightFg2,
      fg3: NeutralPaletteColorName.NightFg3,
    }
  },

  isDark: true,
  semanticColors: {
    error: ExtendedPaletteColorName.Joule,
    negative: ExtendedPaletteColorName.Joule,
    positive: ExtendedPaletteColorName.Prime,
    primary: ExtendedPaletteColorName.Prime,
    scary: ExtendedPaletteColorName.Joule,
    success: ExtendedPaletteColorName.Prime,
  },
  shadowColors: {
    s1: shadowColors.darkBlackShadow,
    s2: shadowColors.blackUnderShadow,
  },
}

/**
 * This is the theme for crypto UI in our apps.
 */
export const cryptoTheme: ProductTheme = {
  ...darkTheme,
  semanticColors: {
    error: ExtendedPaletteColorName.UV,
    negative: ExtendedPaletteColorName.UV,
    positive: ExtendedPaletteColorName.XRay,
    primary: ExtendedPaletteColorName.XRay,
    scary: ExtendedPaletteColorName.UV,
    success: ExtendedPaletteColorName.XRay,
  },
}

/**
 * This is a theme that indicates that the user should proceed with caution.
 */
export const scaryTheme: ProductTheme = {
  getNeutralColorPalette: () => {
    return {
      bg1: NeutralPaletteColorName.ScaryBg1,
      bg2: NeutralPaletteColorName.DayBg1,
      bg3: NeutralPaletteColorName.ScaryBg3,
      fg1: NeutralPaletteColorName.DayBg1,
      fg2: NeutralPaletteColorName.DayBg1,
      fg3: NeutralPaletteColorName.DayBg1,
    }
  },

  isDark: true,
  semanticColors: {
    error: ExtendedPaletteColorName.Nova,
    negative: ExtendedPaletteColorName.Joule,
    positive: ExtendedPaletteColorName.Prime,
    primary: ExtendedPaletteColorName.Nova,
    scary: ExtendedPaletteColorName.Nova,
    success: ExtendedPaletteColorName.Prime,
  },
  shadowColors: {
    s1: shadowColors.blackShadow,
    s2: shadowColors.blackUnderShadow,
  },
}

/**
 * This is the theme for credit card UI in our apps.
 */
export const creditGoldTheme: ProductTheme = {
  ...lightTheme,
  getNeutralColorPalette: () => ({
    bg1: NeutralPaletteColorName.CreditGoldBg1,
    bg2: NeutralPaletteColorName.CreditGoldBg2,
    bg3: NeutralPaletteColorName.CreditGoldBg3,
    fg1: NeutralPaletteColorName.CreditGoldFg1,
    fg2: NeutralPaletteColorName.CreditGoldFg2,
    fg3: NeutralPaletteColorName.CreditGoldFg3,
  }),semanticColors: {
    ...lightTheme.semanticColors,
    primary: ExtendedPaletteColorName.CreditGoldAccent,
  }
}

/**
 * New banking app theme
 */
export const bankingTheme: ProductTheme = {
  ...darkTheme,
  getNeutralColorPalette: () => ({
    bg1: NeutralPaletteColorName.BankingBg1,
    bg2: NeutralPaletteColorName.BankingBg2,
    bg3: NeutralPaletteColorName.BankingBg3,
    fg1: NeutralPaletteColorName.BankingFg1,
    fg2: NeutralPaletteColorName.BankingFg2,
    fg3: NeutralPaletteColorName.BankingFg3,
  }),
  semanticColors: {
    ...darkTheme.semanticColors,
    positive: ExtendedPaletteColorName.BankingAccentLight,
    negative: ExtendedPaletteColorName.BankingAccentDark,
  }
}

/**
 * We use CSS variables for our theme settings in order to be able to easily theme an entire
 * DOM subtree. This is the full set of variable names we use for our theme properties.
 */
export const CssVarNames = {
  dataVisualization: {
    categorical1: '--rh__data-visualization-categorical-1',
    categorical10: '--rh__data-visualization-categorical-10',
    categorical11: '--rh__data-visualization-categorical-11',
    categorical12: '--rh__data-visualization-categorical-12',
    categorical13: '--rh__data-visualization-categorical-13',
    categorical14: '--rh__data-visualization-categorical-14',
    categorical15: '--rh__data-visualization-categorical-15',
    categorical16: '--rh__data-visualization-categorical-16',
    categorical17: '--rh__data-visualization-categorical-17',
    categorical18: '--rh__data-visualization-categorical-18',
    categorical19: '--rh__data-visualization-categorical-19',
    categorical2: '--rh__data-visualization-categorical-2',
    categorical20: '--rh__data-visualization-categorical-20',
    categorical3: '--rh__data-visualization-categorical-3',
    categorical4: '--rh__data-visualization-categorical-4',
    categorical5: '--rh__data-visualization-categorical-5',
    categorical6: '--rh__data-visualization-categorical-6',
    categorical7: '--rh__data-visualization-categorical-7',
    categorical8: '--rh__data-visualization-categorical-8',
    categorical9: '--rh__data-visualization-categorical-9',
  },
  focus: {
    innerColor: '--rh__focus-inner-color',
    outerColor: '--rh__focus-outer-color',
  },
  neutral: {
    bg1: '--rh__neutral-bg1',
    bg2: '--rh__neutral-bg2',
    bg3: '--rh__neutral-bg3',
    fg1: '--rh__neutral-fg1',
    fg2: '--rh__neutral-fg2',
    fg3: '--rh__neutral-fg3',
    hoverBg1: '--rh__neutral-hover-bg1',
    hoverBg2: '--rh__neutral-hover-bg2',
    hoverBg3: '--rh__neutral-hover-bg3',
    hoverFg1: '--rh__neutral-hover-fg1',
    hoverFg2: '--rh__neutral-hover-fg2',
    hoverFg3: '--rh__neutral-hover-fg3',
    pressedBg1: '--rh__neutral-pressed-bg1',
    pressedBg2: '--rh__neutral-pressed-bg2',
    pressedBg3: '--rh__neutral-pressed-bg3',
    pressedFg1: '--rh__neutral-pressed-fg1',
    pressedFg2: '--rh__neutral-pressed-fg2',
    pressedFg3: '--rh__neutral-pressed-fg3',
  },
  overlay: {
    base: '--rh__primary-textOverlay',
    light: '--rh__primary-light-textOverlay',
    lightest: '--rh__primary-lightest-textOverlay',
  },
  semantic: {
    error: {
      base: '--rh__semantic-error-base',
      light: '--rh__semantic-error-light',
    },
    negative: {
      base: '--rh__semantic-negative-base',
      light: '--rh__semantic-negative-light',
    },
    positive: {
      base: '--rh__semantic-positive-base',
      light: '--rh__semantic-positive-light',
    },
    primary: {
      base: '--rh__primary-base',
      hover: '--rh__primary-hover',
      light: '--rh__primary-light-base',
      lightest: '--rh__primary-lightest-base',
      pressed: '--rh__primary-pressed',
    },
    scary: {
      base: '--rh__semantic-scary-base',
      light: '--rh__semantic-scary-light',
    },
    success: {
      base: '--rh__semantic-success-base',
      light: '--rh__semantic-success-light',
    },
  },
  shadow: {
    s1: '--rh__shadow__s1',
    s2: '--rh__shadow__s2',
  },
  size: SizePaletteCssVarNames,
  textColor: '--rh__text-color',
  type: TypePaletteCssVarNames,
}

/**
 * A type for all known color names.
 */
export type ColorToken =
  | SemanticColorToken
  | ThemeColorToken
  | OverlayColorToken

/**
 * Tokens for easily referencing the colors we use for text overlaid on a primary color.
 */
export enum OverlayColorToken {
  Base = 'overlay.base',
  Light = 'overlay.light',
  Lightest = 'overlay.lightest',
}

/**
 * Tokens for easily referencing our foreground and background colors.
 */
export enum NeutralColorToken {
  Fg1 = 'neutral.fg1',
  Fg2 = 'neutral.fg2',
  Fg3 = 'neutral.fg3',
  Bg3 = 'neutral.bg3',
  Bg2 = 'neutral.bg2',
  Bg1 = 'neutral.bg1',
}

/**
 * Returns a boolean indicating whether the specified string is a valid token in our
 * neutral palette. Also performs type narrowing.
 */
export function getIsNeutralColorToken(str: string): str is NeutralColorToken {
  return Object.values(NeutralColorToken).includes(str as NeutralColorToken)
}

/**
 * Tokens for easily referencing our semantic colors.
 */
export enum SemanticColorToken {
  Primary = 'semantic.primary',
  Positive = 'semantic.positive',
  Negative = 'semantic.negative',
  Success = 'semantic.success',
  Error = 'semantic.error',
  Scary = 'semantic.scary',
}

/**
 * Returns a boolean indicating whether the specified string is a valid token in our
 * semantic palette. Also performs type narrowing.
 */
export function getIsSemanticColorToken(
  str: string
): str is SemanticColorToken {
  return Object.values(SemanticColorToken).includes(str as SemanticColorToken)
}

/**
 * In most use cases we actually want the full CSS variable (`var(--my-var)`) and not just
 * the name (`--my-var`), so we export them in this form as well.
 */
export const CssVars: typeof CssVarNames = getWrappedCssVars(CssVarNames)

// The types can't fully capture this, but the intent is that whichever
// WrappableValue "subtype" you provide, you'll get the same one back.
type WrappableValue = undefined | string | { [k: string]: WrappableValue }
export function getWrappedCssVars<T extends WrappableValue>(valueToWrap: T): T
export function getWrappedCssVars(valueToWrap: WrappableValue): WrappableValue {
  // If it's undefined, just preserve it.
  if (typeof valueToWrap === 'undefined') {
    return undefined
  }

  // If it's a string, wrap it.
  if (typeof valueToWrap === 'string') {
    return `var(${valueToWrap})`
  }

  // Otherwise it's an object, so return a new object whose properties have all
  // been wrapped.
  return Object.entries(valueToWrap).reduce(
    (acc, [key, val]) => ({
      ...acc,
      [key]: getWrappedCssVars(val),
    }),
    {}
  )
}

export type LinearGradientColor = {
  angle: number
  colorStops: {
    color: SemanticColorToken | ThemeColorToken
    stop: number
  }[]
}

export function isLinearGradientColor(
  color: ColorToken | LinearGradientColor | undefined | null
): color is LinearGradientColor {
  return Boolean(
    color && color.hasOwnProperty('angle') && color.hasOwnProperty('colorStops')
  )
}

const LIGHT_THEME_CRYPTO_UP_COLOR = ExtendedPaletteColorName.XRay
const LIGHT_THEME_ACCESSIBLE_CRYPTO_DOWN_COLOR = ExtendedPaletteColorName.Iris
const ACCESSIBLE_STOCK_UP_COLOR = ExtendedPaletteColorName.Prime
const ACCESSIBLE_STOCK_DOWN_COLOR = ExtendedPaletteColorName.UV
const ACCESSIBLE_CRYPTO_UP_COLOR = ExtendedPaletteColorName.XRay
const ACCESSIBLE_CRYPTO_DOWN_COLOR = ExtendedPaletteColorName.Iris
const ROBINHOOD_GOLD_COLOR = ExtendedPaletteColorName.Sol

/**
 * This is a variant of our light theme for:
 *    * The UI should reflect a value that's up.
 */
export const lightUpTheme = lightTheme

/**
 * This is a variant of our light theme for:
 *    * The UI should reflect a value that's down.
 */
export const lightDownTheme: ProductTheme = createDownTheme(lightUpTheme)

/**
 * This is a variant of our light theme for:
 *    * The UI should reflect a value that's up.
 *    * A11y to users with colorblindness.
 */
export const lightUpAccessibleColorsTheme: ProductTheme = {
  ...lightTheme,
  semanticColors: {
    error: ACCESSIBLE_STOCK_DOWN_COLOR,
    negative: ACCESSIBLE_STOCK_DOWN_COLOR,
    positive: ACCESSIBLE_STOCK_UP_COLOR,
    primary: ACCESSIBLE_STOCK_UP_COLOR,
    scary: ACCESSIBLE_STOCK_DOWN_COLOR,
    success: ACCESSIBLE_STOCK_UP_COLOR,
  },
}

/**
 * This is a variant of our light theme for:
 *    * The UI should reflect a value that's down.
 *    * A11y to users with colorblindness.
 */
export const lightDownAccessibleColorsTheme: ProductTheme = createDownTheme(
  lightUpAccessibleColorsTheme
)

/**
 * This is a variant of our light theme for:
 *    * The UI is colored for Robinhood Gold
 */
export const lightGoldTheme: ProductTheme = {
  ...lightTheme,
  semanticColors: {
    ...lightTheme.semanticColors,
    primary: ROBINHOOD_GOLD_COLOR,
  },
}

/**
 * This is a variant of our light theme for:
 *    * The UI is colored for Robinhood Gold
 *    * A11y to users with colorblindness.
 */
export const lightGoldAccessibleColorsTheme: ProductTheme = {
  ...lightUpAccessibleColorsTheme,
  semanticColors: {
    ...lightUpAccessibleColorsTheme.semanticColors,
    primary: ROBINHOOD_GOLD_COLOR,
  },
}

/**
 * This is a variant of our dark theme for:
 *    * The UI should reflect a value that's up.
 */
export const darkUpTheme = darkTheme

/**
 * This is a variant of our down theme for:
 *    * The UI should reflect a value that's down.
 */
export const darkDownTheme: ProductTheme = createDownTheme(darkTheme)

/**
 * This is a variant of our dark theme for:
 *    * The UI should reflect a value that's up.
 *    * A11y to users with colorblindness.
 */
export const darkUpAccessibleColorsTheme: ProductTheme = {
  ...darkTheme,
  semanticColors: {
    error: ACCESSIBLE_STOCK_DOWN_COLOR,
    negative: ACCESSIBLE_STOCK_DOWN_COLOR,
    positive: ACCESSIBLE_STOCK_UP_COLOR,
    primary: ACCESSIBLE_STOCK_UP_COLOR,
    scary: ACCESSIBLE_STOCK_DOWN_COLOR,
    success: ACCESSIBLE_STOCK_UP_COLOR,
  },
}

/**
 * This is a variant of our dark theme for:
 *    * The UI should reflect a value that's down.
 *    * A11y to users with colorblindness.
 */
export const darkDownAccessibleColorsTheme: ProductTheme = createDownTheme(
  darkUpAccessibleColorsTheme
)

/**
 * This is a variant of our dark theme for:
 *    * The UI is colored for Robinhood Gold
 */
export const darkGoldTheme: ProductTheme = {
  ...darkTheme,
  semanticColors: {
    ...darkTheme.semanticColors,
    primary: ROBINHOOD_GOLD_COLOR,
  },
}

/**
 * This is a variant of our dark theme for:
 *    * The UI is colored for Robinhood Gold
 *    * A11y to users with colorblindness.
 */
export const darkGoldAccessibleColorsTheme: ProductTheme = {
  ...darkUpAccessibleColorsTheme,
  semanticColors: {
    ...darkUpAccessibleColorsTheme.semanticColors,
    primary: ROBINHOOD_GOLD_COLOR,
  },
}

/**
 * This is a variant of our crypto theme for:
 *    * The UI should reflect a value that's up.
 */
export const darkCryptoUpTheme = cryptoTheme

/**
 * This is a variant of our crypto theme for:
 *    * The UI should reflect a value that's down.
 */
export const darkCryptoDownTheme: ProductTheme = createDownTheme(cryptoTheme)

/**
 * This is a variant of our crypto theme for:
 *    * The UI should reflect a value that's up.
 *    * A11y to users with colorblindness.
 */
export const darkCryptoUpAccessibleColorsTheme: ProductTheme = {
  ...cryptoTheme,
  semanticColors: {
    error: ACCESSIBLE_CRYPTO_DOWN_COLOR,
    negative: ACCESSIBLE_CRYPTO_DOWN_COLOR,
    positive: ACCESSIBLE_CRYPTO_UP_COLOR,
    primary: ACCESSIBLE_CRYPTO_UP_COLOR,
    scary: ACCESSIBLE_CRYPTO_DOWN_COLOR,
    success: ACCESSIBLE_CRYPTO_UP_COLOR,
  },
}

/**
 * This is a variant of our crypto theme for:
 *    * The UI should reflect a value that's down.
 *    * A11y to users with colorblindness.
 */
export const darkCryptoDownAccessibleColorsTheme: ProductTheme =
  createDownTheme(darkCryptoUpAccessibleColorsTheme)

/**
 * This is a variant of our crypto theme for:
 *    * The UI should reflect a value that's up.
 *    * The UI should be light- rather dark-colored.
 *
 * This is primarily used for crypto elements within a non-crypto light-themed page.
 */
export const lightCryptoUpTheme: ProductTheme = {
  ...lightTheme,
  semanticColors: {
    error: cryptoTheme.semanticColors.error,
    negative: cryptoTheme.semanticColors.negative,
    positive: LIGHT_THEME_CRYPTO_UP_COLOR,
    primary: LIGHT_THEME_CRYPTO_UP_COLOR,
    scary: cryptoTheme.semanticColors.scary,
    success: cryptoTheme.semanticColors.success,
  },
}

/**
 * This is a variant of our crypto theme for:
 *    * The UI should reflect a value that's down.
 *    * The UI should be light- rather dark-colored.
 *
 * This is primarily used for crypto elements within a non-crypto light-themed page.
 */
export const lightCryptoDownTheme: ProductTheme =
  createDownTheme(lightCryptoUpTheme)

/**
 * This is a variant of our crypto theme for:
 *    * The UI should reflect a value that's up.
 *    * The UI should be light- rather dark-colored.
 *    * A11y to users with colorblindness.
 *
 * This is primarily used for crypto elements within a non-crypto light-themed page.
 */
export const lightCryptoUpAccessibleColorsTheme: ProductTheme = {
  ...cryptoTheme,
  semanticColors: {
    error: LIGHT_THEME_ACCESSIBLE_CRYPTO_DOWN_COLOR,
    negative: LIGHT_THEME_ACCESSIBLE_CRYPTO_DOWN_COLOR,
    positive: LIGHT_THEME_CRYPTO_UP_COLOR,
    primary: LIGHT_THEME_CRYPTO_UP_COLOR,
    scary: LIGHT_THEME_ACCESSIBLE_CRYPTO_DOWN_COLOR,
    success: LIGHT_THEME_CRYPTO_UP_COLOR,
  },
}

/**
 * This is a variant of our crypto theme for:
 *    * The UI should reflect a value that's down.
 *    * The UI should be light- rather dark-colored.
 *    * A11y to users with colorblindness.
 *
 * This is primarily used for crypto elements within a non-crypto light-themed page.
 */
export const lightCryptoDownAccessibleColorsTheme: ProductTheme =
  createDownTheme(lightCryptoUpAccessibleColorsTheme)

/**
 * This is a variant of our scary theme for:
 *    * A11y to users with colorblindness.
 */
export const scaryAccessibleColorsTheme: ProductTheme = {
  getNeutralColorPalette: () => {
    return {
      bg1: NeutralPaletteColorName.ScaryAccessibleColorsBg1,
      bg2: NeutralPaletteColorName.DayBg1,
      bg3: NeutralPaletteColorName.ScaryAccessibleColorsBg3,
      fg1: NeutralPaletteColorName.DayBg1,
      fg2: NeutralPaletteColorName.DayBg1,
      fg3: NeutralPaletteColorName.DayBg1,
    }
  },

  isDark: true,
  semanticColors: {
    error: ExtendedPaletteColorName.Nova,
    negative: ExtendedPaletteColorName.UV,
    positive: ExtendedPaletteColorName.Prime,
    primary: ExtendedPaletteColorName.Nova,
    scary: ExtendedPaletteColorName.Nova,
    success: ExtendedPaletteColorName.Prime,
  },
  shadowColors: {
    s1: shadowColors.blackShadow,
    s2: shadowColors.blackUnderShadow,
  },
}

/**
 * Helper method to create a down variant of the provide base theme.
 */
function createDownTheme(theme: ProductTheme): ProductTheme {
  return {
    ...theme,
    semanticColors: {
      ...theme.semanticColors,
      primary: theme.semanticColors.negative,
    },
  }
}
